/** @format */

import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { showNotification } from "@mantine/notifications";
import {
  ActionIcon,
  Badge,
  Button,
  Pagination,
  Select,
  Skeleton,
  Table,
  Text,
  TextInput,
  Modal,
} from "@mantine/core";
import { BeatLoader } from "react-spinners";
import { DatePicker } from "@mantine/dates";
import {
  Eye,
  History,
  Plus,
  SortAscending,
  SortDescending,
} from "tabler-icons-react";
import { formatISO } from "date-fns";
import {
  deleteAgreementBulk,
  getAgreementsDocsLockerCsvDownload,
} from "../../utilities/apis/agreements";
import {
  loadingStates,
  csvDownload,
  formatDate,
  formatIndianNumber,
} from "../../utilities/utilities";
import { apiWrapWithErrorWithData } from "../../utilities/apiHelpers";
import { getDocLockerList } from "../../utilities/apis/agreements";
import PartyNameFilter, {
  PARTY_NAME_LIST_TYPES,
} from "../../components/PartyNameFilter";
import AgreementTypeFilter from "../../components/AgreementTypeFilter";
import { getStatusColor, getStatusText } from "../../utilities/enums";
import colors from "../../utilities/design";
import Styles from "./AgreementsListingPage.module.css";
import { selectUserData } from "../../redux/selectors";
import { useSelector } from "react-redux";
import AgreementAuditLogModal from "./AgreementAuditLogModal";
import RelatedAgreementModal from "./agreementRelatedModal";
import { getBussinessUnit } from "../../utilities/apis/serviceProvider";

const SORT_OPTIONS = [
  { label: "created at", value: "createdAt" },
  { label: "end date", value: "dueDateOfContract" },
  { label: "type of agreement", value: "typeOfAgreement" },
  { label: "agreement value", value: "agreementValue" },
  { label: "ain", value: "id" },
];

const INITIAL_FILTERS = {
  sortBy: "id",
  sortDirection: "desc",
  statusType: null,
  estamp: null,
  agreementType: null,
  location: null,
  startDate: null,
  endDate: null,
  partyName: "",
  partyAddress: "",
  businessunit: null,
};

const statusOptions = [
  // {
  //   label: "INTIATED",
  //   value: "CREATED",
  // },
  // {
  //   label: "UNDER REVIEW",
  //   value: "Review",
  // },
  // {
  //   label: "PENDING APPROVAL",
  //   value: "Approval",
  // },
  {
    label: "FINALISED",
    value: "Signature",
  },
  {
    label: "RE-FINALIZED",
    value: "RE-SIGNATURE",
  },
  {
    label: "PENDING SIGNATURE",
    value: "SENT",
  },
  {
    label: "PARTIALLY COMPLETED",
    value: "PARTIALLYCOMPLETED",
  },
  {
    label: "CANCELLED",
    value: "CANCELLED",
  },
  {
    label: "DECLINED",
    value: "DECLINED",
  },
  {
    label: "SIGNED",
    value: "SIGNED",
  },
  {
    label: "FORCECLOSED",
    value: "FORCECLOSED",
  },
];
const estampOption = [
  {
    label: "Yes",
    value: "Yes",
  },
  {
    label: "No",
    value: "No",
  },
];

const DocsLocker = () => {
  const navigate = useNavigate();
  const currentUser = useSelector(selectUserData) || {};

  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);
  const [listing, setListing] = useState({
    list: null,
    agreementsCount: null,
    loaded: false,
  });
  const [businessUnitOptions, setBusinessUnitOptions] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(loadingStates.NO_ACTIVE_REQUEST);
  const [exportButtonLoading, setExportButtonLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [agreementId, setAgreementId] = useState(null);
  const [showModalRelated, setShowModalRelated] = useState(false);
  const [relatedAgreementId, setRelatedAgreementId] = useState(null);

  useEffect(() => {
    fetchBusinessUnits();
  }, []);

  useEffect(() => {
    fetchAgreementsList({ page });
  }, [page, pageSize, appliedFilters, filters.sortBy, filters.sortDirection]);

  const fetchBusinessUnits = async () => {
    try {
      const { data } = await getBussinessUnit();
      if (data?.result) {
        setBusinessUnitOptions(
          data.result.map((unit) => ({
            value: String(unit.id),
            label: unit.name,
          })),
        );
      }
    } catch (error) {
      console.error("Error fetching business units:", error);
    }
  };

  const buildFilterOptions = useCallback((filterState) => {
    if (
      (filterState.startDate && !filterState.endDate) ||
      (!filterState.startDate && filterState.endDate)
    ) {
      showNotification({
        title: "Agreement notification",
        message: "Please select both start and end date",
        color: "red",
      });
      return null;
    }

    const filterOptions = {};

    if (filterState.startDate && filterState.endDate) {
      filterOptions.createdAt = {
        gte: formatISO(filterState.startDate),
        lte: formatISO(filterState.endDate),
      };
    }
    if (filterState.agreementType) {
      filterOptions.typeOfAgreement = { equals: filterState.agreementType };
    }
    if (filterState.statusType) {
      filterOptions.status = { equals: filterState.statusType };
    }
    if (filterState.estamp) {
      filterOptions.agreementType = {
        equals: filterState.estamp === "Yes" ? "eStamp" : "normal",
      };
    }
    if (filterState.businessunit) {
      filterOptions.mapBusinessunit = { equals: filterState.businessunit };
    }
    if (filterState.location) {
      filterOptions.location = filterState.location;
    }
    if (filterState.partyName?.length > 0) {
      filterOptions.OR = [
        {
          secondParty: {
            path: "$.name",
            string_contains: filterState.partyName,
          },
        },
        {
          secondParty: {
            path: "$.indi_name",
            string_contains: filterState.partyName,
          },
        },
        {
          secondParty: {
            path: "$.brandName",
            string_contains: filterState.partyName,
          },
        },
      ];
    }
    if (filterState.partyAddress?.trim()) {
      const term = filterState.partyAddress.trim();
      filterOptions.OR = [
        ...(filterOptions.OR || []),
        { secondParty: { path: "$.address", string_contains: term } },
        { secondParty: { path: "$.corporateAddress", string_contains: term } },
        { secondParty: { path: "$.indi_address", string_contains: term } },
      ];
    }

    return filterOptions;
  }, []);

  const buildListPayload = useCallback(
    (filterState, reqPage) => {
      const filterOptions = buildFilterOptions(filterState);
      if (filterOptions === null) return null;

      return {
        page: reqPage ?? page,
        pageSize,
        sortByOptions: { [filters.sortBy]: filters.sortDirection },
        filterOptions,
      };
    },
    [buildFilterOptions, filters.sortBy, filters.sortDirection, page, pageSize],
  );

  const fetchAgreementsList = async ({
    page: reqPage,
    filterState = appliedFilters,
  } = {}) => {
    const payload = buildListPayload(filterState, reqPage);
    if (!payload) return;

    setLoading(loadingStates.LOADING);

    const response = await apiWrapWithErrorWithData(
      getDocLockerList({ ...payload }),
    );

    if (response?.success && response?.agreements) {
      setListing({
        list: response.agreements,
        agreementsCount: response.agreementsCount,
        loaded: true,
      });
    }

    setLoading(loadingStates.NO_ACTIVE_REQUEST);
  };

  const handleFilterChange = (key) => (val) =>
    setFilters((prev) => ({ ...prev, [key]: val }));

  const handleSearch = () => {
    if (buildFilterOptions(filters) === null) return;
    setAppliedFilters({ ...filters });
    setPage(1);
  };

  const clearHandler = () => {
    setFilters(INITIAL_FILTERS);
    setAppliedFilters(INITIAL_FILTERS);
    setPage(1);
  };

  const handleCheckboxChange = (id, isChecked) => {
    setSelectedIds((prevSelectedIds) => {
      if (isChecked) {
        return [...prevSelectedIds, id];
      } else {
        return prevSelectedIds.filter((selectedId) => selectedId !== id);
      }
    });
  };

  const deleteHandler = async () => {
    const agreementIds = selectedIds;
    setDeleteLoading(true);
    const response = await apiWrapWithErrorWithData(
      deleteAgreementBulk({ agreementIds }),
    );
    if (response?.success) {
      showNotification({
        color: "green",
        title: "Agreements",
        message: "Agreement deleted successfully.",
      });
      setOpen(false);
      navigate("/app");
    } else {
      showNotification({
        color: "red",
        title: "Agreements",
        message: "Unable to delete agreement",
      });
    }
    setDeleteLoading(false);
  };

  const data = listing?.list;

  const handleCsvDownload = async () => {
    const exportPayload = buildListPayload(appliedFilters, 1);
    if (!exportPayload) return;

    try {
      setExportButtonLoading(true);
      const response = await apiWrapWithErrorWithData(
        getAgreementsDocsLockerCsvDownload({
          page: exportPayload.page,
          sortByOptions: exportPayload.sortByOptions,
          filterOptions: exportPayload.filterOptions,
        }),
      );
      if (response?.success && response?.agreements?.length > 0) {
        csvDownload({
          data: response.agreements,
          headers: Object.keys(response.agreements[0]),
          name: "docs locker agreements",
        });
      } else {
        showNotification({
          title: "Agreement csv download",
          message: "No agreement available",
          color: "red",
        });
      }
    } catch {
      showNotification({
        title: "Agreement csv download",
        message: "Could not load agreement csv",
        color: "red",
      });
    } finally {
      setExportButtonLoading(false);
    }
  };

  const handleSearchClick = (id) => {
    setShowModal(true);
    setAgreementId(id);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handlePlusClick = (id) => {
    setShowModalRelated(true);
    setRelatedAgreementId(id);
  };

  const handleCloseRelatedAgreementModal = () => {
    setShowModalRelated(false);
  };

  return (
    <>
      <div className="pb-2 w-full">
        <h2>Docs Locker</h2>
      </div>

      {showModalRelated && (
        <Modal
          overflow="inside"
          opened
          onClose={handleCloseRelatedAgreementModal}
          size="calc(80vw)"
        >
          <RelatedAgreementModal agreementId={relatedAgreementId} />
        </Modal>
      )}

      <div className="grid grid-cols-4 mb-2 gap-3">
        <Select
          placeholder="Agreement status"
          value={filters.statusType}
          onChange={handleFilterChange("statusType")}
          data={statusOptions}
          clearable
        />
        <Select
          placeholder="eStamp"
          value={filters.estamp}
          onChange={handleFilterChange("estamp")}
          data={estampOption}
          clearable
        />
        <AgreementTypeFilter
          value={filters.agreementType}
          onChange={handleFilterChange("agreementType")}
        />
        <Select
          placeholder="Business unit"
          value={filters.businessunit}
          data={businessUnitOptions}
          onChange={handleFilterChange("businessunit")}
          clearable
        />
        <DatePicker
          placeholder="Created date (from)"
          value={filters.startDate}
          onChange={handleFilterChange("startDate")}
          minDate={new Date(new Date().setMonth(new Date().getMonth() - 3))}
        />
        <DatePicker
          placeholder="Created date (to)"
          value={filters.endDate}
          onChange={handleFilterChange("endDate")}
          minDate={filters.startDate}
          disabled={!filters.startDate}
        />
        <PartyNameFilter
          value={filters.partyName}
          onChange={(val) =>
            setFilters((prev) => ({ ...prev, partyName: val }))
          }
          listType={PARTY_NAME_LIST_TYPES.DOC_LOCKER}
        />
        <TextInput
          placeholder="State / City"
          value={filters.partyAddress}
          onChange={(e) => {
            const value = e.target.value;
            setFilters((prev) => ({ ...prev, partyAddress: value }));
          }}
        />
      </div>

      <div className="flex items-center justify-between my-4">
        <div className="flex gap-2 flex-wrap">
          <Button color="green" onClick={handleSearch}>
            Search
          </Button>
          <Button color="red" onClick={clearHandler}>
            Clear
          </Button>
          {currentUser?.loginAs?.id === 1 && (
            <Button
              disabled={deleteLoading || selectedIds.length === 0}
              style={{
                backgroundColor: deleteLoading ? "#ffcccc" : "#ff4d4d",
                color: "#F5F5F5",
              }}
              onClick={deleteHandler}
            >
              {deleteLoading ? (
                <BeatLoader size={10} color={colors.rejected} />
              ) : (
                "Delete"
              )}
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Select
            value={filters.sortBy}
            onChange={handleFilterChange("sortBy")}
            data={SORT_OPTIONS}
          />
          <ActionIcon
            onClick={() =>
              setFilters((prev) => ({
                ...prev,
                sortDirection: prev.sortDirection === "asc" ? "desc" : "asc",
              }))
            }
          >
            {filters.sortDirection === "asc" ? (
              <SortAscending size={32} />
            ) : (
              <SortDescending size={32} />
            )}
          </ActionIcon>
          <Button
            disabled={exportButtonLoading}
            onClick={handleCsvDownload}
            style={{ backgroundColor: "#46BDE1" }}
          >
            Export
          </Button>
        </div>
      </div>

      <Modal
        overflow="inside"
        closeOnClickOutside={false}
        size="calc(30vw)"
        opened={open}
        onClose={() => setOpen(!open)}
        title="Delete Agreement"
      />
      {showModal && (
        <Modal
          overflow="inside"
          opened
          onClose={handleCloseModal}
          size="calc(80vw)"
        >
          <AgreementAuditLogModal agreementId={agreementId} />
        </Modal>
      )}

      {loading === loadingStates.LOADING && (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 10 }).map((i) => (
            <Skeleton key={i} height={30} radius="md" className=" w-full" />
          ))}
          <div className="flex flex-row justify-center">
            <Skeleton height={40} radius="md" className="w-40" />
          </div>
        </div>
      )}
      {listing.loaded &&
        loading !== loadingStates.LOADING &&
        Boolean(data?.length) && (
          <div className={Styles.tableScrollWrapper}>
            <Table striped>
              <thead>
                <tr>
                  <th>AR NO.</th>
                  <th>Type</th>
                  <th>Business Unit</th>
                  <th>Status</th>
                  <th>Second Party</th>
                  <th>Contract Date</th>
                  <th>End Date</th>
                  {/* <th>TN Period</th> */}
                  <th colSpan={3}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data
                  .filter((v) => v.agreementData?.json?.typeOfAgreement)
                  .map((row) => (
                    <tr key={row.id}>
                      <td>{`AR-${row.id}`}</td>
                      <td>{row.agreementData.json.typeOfAgreement}</td>
                      <td>{row.agreementData?.json?.location || "—"}</td>
                      <td>
                        <Badge color={getStatusColor(row.status)}>
                          {getStatusText(row.status)}
                        </Badge>
                      </td>
                      <td>
                        {row.agreementData.json.brandName}
                        <br />
                        <span className="font-light">
                          {`(${row.agreementData?.json?.secondParty?.name || "-"})`}
                        </span>
                      </td>
                      <td>{formatDate(row.dateOfContract)}</td>
                      <td>{formatDate(row.dueDateOfContract)}</td>
                      {/* <td>{row.terminationNoticePeriod ?? "—"}</td> */}
                      <td style={{ textAlign: "center", padding: "10px 2px" }}>
                        {row.relationshipWith ? (
                          <ActionIcon
                            onClick={() => handlePlusClick(row.id)}
                            color="white"
                          >
                            <Plus color="#46BDE1" size={24} />
                          </ActionIcon>
                        ) : (
                          <span>--</span>
                        )}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <Link
                          to={`/app/agreements_manager/agreements/details/${row.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Eye color="#46BDE1" size={24} />
                        </Link>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {!row.agreementData?.json?.isLegacy ? (
                          <ActionIcon
                            onClick={() => handleSearchClick(row.id)}
                            color="white"
                          >
                            <History color="#46BDE1" size={24} />
                          </ActionIcon>
                        ) : (
                          "NA"
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </div>
        )}
      {listing.loaded && loading !== loadingStates.LOADING && !data?.length ? (
        <div
          className="flex justify-center items-center"
          style={{
            minHeight: "300px",
          }}
        >
          No agreements
        </div>
      ) : listing.agreementsCount ? (
        <div className="flex flex-row justify-center items-center my-4 gap-3">
          <Pagination
            page={page}
            onChange={setPage}
            total={Math.ceil(listing.agreementsCount / pageSize)}
          />
          <Text>Page size</Text>
          <Select
            className="w-20"
            data={["10", "20", "50"]}
            value={String(pageSize)}
            onChange={(val) => {
              setPageSize(parseInt(val));
              setPage(1);
            }}
          />
        </div>
      ) : null}
      <Modal
        opened={open}
        onClose={() => setOpen(false)}
        size="calc(30vw)"
        style={{ marginTop: "12%" }}
        title="Select Agreement Type"
      >
        <div className={Styles["popup-desig"]}>
          <p
            style={{
              cursor: "pointer",
              border: "2px solid lightblue",
              padding: "8px",
              margin: "0",
              marginBottom: "8px",
            }}
            onClick={() => navigate("/app/agreements_manager/agreements/new")}
          >
            Individual Agreement
          </p>
          <p
            style={{
              cursor: "pointer",
              border: "2px solid lightblue",
              padding: "8px",
              margin: "0",
            }}
            onClick={() =>
              navigate("/app/agreements_manager/agreements/new-bulk")
            }
          >
            Bulk Agreement
          </p>
          <div style={{ textAlign: "center", paddingTop: "3%" }}>
            <button
              style={{
                cursor: "pointer",
                background: "red",
                border: "none",
                padding: "8px",
                color: "white",
                margin: "5px 0",
                borderRadius: "10%",
              }}
              onClick={() => setOpen(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default DocsLocker;
